import { useSyncExternalStore } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity,
  Bell,
  BookOpen,
  ChartColumn,
  CreditCard,
  ScrollText,
  Send,
  Settings,
  Users,
  type LucideIcon,
} from "lucide-react";
import { isAlertingEnabled, isBillingEnabled } from "@/lib/features";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { TENANT_TIER } from "@/lib/tenant";
import { UserProfileMenu } from "@/components/shared/UserProfileMenu";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** Shared focus ring for every interactive element in the chrome. */
const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-k-accent";

const DOCS_URL = `${import.meta.env.VITE_DOCS_URL ?? "https://kopai.app"}/docs/quickstart/`;

/**
 * Observability tab a nav item maps to. `null` is the default tab (Traces),
 * which the embedded page renders whenever `?tab=` is absent or unrecognised.
 */
type ObservabilityTab = "logs" | "metrics" | null;

interface NavItem {
  title: string;
  icon: LucideIcon;
  path: string;
  tab?: ObservabilityTab;
}

/**
 * Built per render rather than at module load: the billing entry is
 * gated on a deploy-time flag and the tenant's tier, the alerting group on
 * its own flag plus the workspace's `alertingEnabled`, and reading them here keeps the nav and the route table
 * (see `router.tsx`) in agreement.
 */
function buildNavGroups({
  showBilling,
  showAlerting,
}: {
  showBilling: boolean;
  showAlerting: boolean;
}): { label: string; items: NavItem[] }[] {
  return [
    {
      label: "Observe",
      items: [
        {
          title: "Traces",
          icon: Activity,
          path: "/observability",
          tab: null,
        },
        {
          title: "Logs",
          icon: ScrollText,
          path: "/observability?tab=logs",
          tab: "logs",
        },
        {
          title: "Metrics",
          icon: ChartColumn,
          path: "/observability?tab=metrics",
          tab: "metrics",
        },
      ],
    },
    ...(showAlerting
      ? [
          {
            label: "Alerting",
            items: [
              { title: "Rules", icon: Bell, path: "/alerts/rules" },
              { title: "Targets", icon: Send, path: "/alerts/targets" },
            ],
          },
        ]
      : []),
    {
      label: "Workspace",
      items: [
        { title: "Team", icon: Users, path: "/team" },
        ...(showBilling
          ? [{ title: "Billing", icon: CreditCard, path: "/billing" }]
          : []),
        { title: "Settings", icon: Settings, path: "/settings" },
      ],
    },
  ];
}

function subscribeToHistory(onChange: () => void) {
  window.addEventListener("popstate", onChange);
  return () => window.removeEventListener("popstate", onChange);
}

function getSearchSnapshot() {
  return window.location.search;
}

function getServerSearchSnapshot() {
  return "";
}

/** Normalises `?tab=` to the tab the embedded observability page will show. */
function readObservabilityTab(search: string): ObservabilityTab {
  const tab = new URLSearchParams(search).get("tab");
  return tab === "logs" || tab === "metrics" ? tab : null;
}

function isNavItemActive(
  item: NavItem,
  pathname: string,
  tab: ObservabilityTab,
): boolean {
  if (item.tab !== undefined) {
    return pathname === "/observability" && tab === item.tab;
  }
  // Nested pages such as /alerts/rules/new keep their section lit.
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

export function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useAuth();

  // The Billing entry is hidden wholesale where billing hasn't launched
  // (the route is absent there too — see `router.tsx` — so it would
  // 404), and for dedicated-tier tenants, whose billing is a managed
  // agreement with nothing self-serve behind the page. Tier is only
  // known once the workspace read lands, so the entry fails open: while
  // loading (or on error) it stays visible, and a dedicated tenant who
  // clicks through gets the page's own not-found state (`Billing.tsx`)
  // — the same thing a direct /billing link shows them.
  //
  // The Alerting group needs two yeses: the deploy-time flag, and the
  // workspace endpoint saying this tenant is on the alerting allow-list.
  // Unlike billing it fails closed while the workspace read is loading,
  // so a tenant outside the beta never sees the group flash in; a direct
  // /alerts link still gets the API's own "not enabled" page.
  //
  // Read from the workspace endpoint, not the billing overview. Tier is
  // derived from `tenants.pool_seq` and never touches the payment
  // provider, but the billing overview does — for any tenant that has
  // ever checked out — so asking it here put a live provider call in
  // front of every authenticated page load, to render a nav item.
  const billingEnabled = isBillingEnabled();
  const alertingEnabled = isAlertingEnabled();
  const { data: tenant } = useTenant({
    enabled: billingEnabled || alertingEnabled,
  });
  const navGroups = buildNavGroups({
    showBilling: billingEnabled && tenant?.tier !== TENANT_TIER.dedicated,
    showAlerting: alertingEnabled && tenant?.alertingEnabled === true,
  });

  // The embedded @kopai/ui observability page drives its tab state straight
  // through `history.pushState` + a synthetic `popstate`, bypassing react-router.
  // Reading the live URL keeps the sidebar's active item in sync with it.
  const search = useSyncExternalStore(
    subscribeToHistory,
    getSearchSnapshot,
    getServerSearchSnapshot,
  );
  const tab = readObservabilityTab(search);

  // ...and the same event has to be fired the other way round, so the embedded
  // page re-reads the URL when navigation starts here.
  const handleNavigate = (path: string) => {
    navigate(path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-k-page">
        <Sidebar className="border-k-line">
          <SidebarHeader className="gap-0 px-[14px] pb-0 pt-[18px]">
            <a
              href="/"
              className={cn(
                "flex w-fit items-center gap-[7px] rounded-lg px-2 py-[2px] no-underline",
                "transition-opacity duration-160 ease-k-out hover:opacity-90",
                FOCUS_RING,
              )}
            >
              <span className="font-mono text-[13px] font-bold text-k-accent">
                {"|--k>"}
              </span>
              <span className="font-mono text-[14px] font-bold text-k-ink-hi">
                KOPAI
              </span>
            </a>
          </SidebarHeader>
          <SidebarContent className="gap-0 px-[14px] pt-[20px]">
            {navGroups.map((group, index) => (
              <SidebarGroup
                key={group.label}
                className={cn("p-0", index > 0 && "mt-6")}
              >
                <div className="px-[10px] pb-[4px] pt-[10px] font-k-sans text-[11px] font-bold uppercase tracking-[0.145em] text-k-ink-3">
                  {group.label}
                </div>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          onClick={() => handleNavigate(item.path)}
                          isActive={isNavItemActive(
                            item,
                            location.pathname,
                            tab,
                          )}
                          className={cn(
                            "h-[33px] gap-[10px] rounded-lg px-[10px] py-2",
                            "font-k-display text-[13px] font-medium text-k-ink-2",
                            "transition-colors duration-160 ease-k-out",
                            "[&>svg]:size-[15px] [&>svg]:text-k-ink-3",
                            "hover:bg-k-surface hover:text-k-ink",
                            "data-[active=true]:bg-k-accent-soft data-[active=true]:font-semibold data-[active=true]:text-k-accent-ink",
                            // Hold the active tint while the item is pressed —
                            // the primitive's own `active:` styles would flash
                            // the hover surface underneath it.
                            "data-[active=true]:active:bg-k-accent-soft data-[active=true]:active:text-k-accent-ink",
                            // Variant order matters: `[&>svg]:` last would put
                            // [data-active] on the <svg> instead of the button.
                            "[&>svg]:data-[active=true]:text-k-accent",
                            FOCUS_RING,
                            "focus-visible:ring-0",
                          )}
                        >
                          <item.icon />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>
          <SidebarFooter className="px-[14px] pb-[18px] pt-2">
            {loading ? (
              <Skeleton className="h-[46px] w-full rounded-[10px]" />
            ) : (
              <UserProfileMenu />
            )}
          </SidebarFooter>
        </Sidebar>
        <div className="flex flex-1 flex-col">
          <header className="sticky top-0 z-10 flex h-11 items-center gap-2.5 border-b border-k-line bg-k-page px-4">
            <SidebarTrigger
              className={cn(
                "h-[30px] w-[30px] rounded-lg text-k-ink-2",
                "duration-160 ease-k-out",
                "[&_svg]:size-[17px]",
                "hover:bg-k-surface hover:text-k-ink",
                "motion-safe:active:scale-[0.94]",
                FOCUS_RING,
                "focus-visible:ring-0",
              )}
            />
            <div className="ml-auto flex items-center gap-2.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "flex h-[30px] w-[30px] items-center justify-center rounded-full",
                      "border border-k-line-2 bg-k-card text-k-ink-2",
                      "transition-all duration-160 ease-k-out",
                      "hover:border-k-ink-3 hover:text-k-ink-hi",
                      "motion-safe:active:scale-[0.94]",
                      FOCUS_RING,
                    )}
                  >
                    <BookOpen className="h-[15px] w-[15px]" />
                    <span className="sr-only">Documentation</span>
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Documentation</p>
                </TooltipContent>
              </Tooltip>
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-auto p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
